// passkey-enroll-decision.ts — PURE gating + messaging logic for the shared
// post-login passkey-enroll step. Kept dependency-free (no DOM, no api, no
// WebAuthn) so it is unit-testable in a plain Node environment. The orchestrator
// (passkey-enroll.ts) consumes these and adds the side effects.

// EnrollDecision is the pure branch chosen BEFORE any side effect runs:
//   - 'skip'   : user didn't opt in — do nothing, just proceed to destination.
//   - 'inform' : opted in but account already has >=1 passkey — show a brief
//                informational message, then proceed (no create).
//   - 'create' : opted in AND zero existing passkeys — run the create ceremony.
export type EnrollDecision = 'skip' | 'inform' | 'create';

// decideEnrollAction is the pure gating rule shared by BOTH the join-code and
// recovery-key flows. Requirement B: create ONLY when the checkbox is checked
// AND existing_passkeys === 0; if the account already has a passkey, inform and
// proceed; otherwise skip.
export function decideEnrollAction(checkboxChecked: boolean, existingPasskeys: number): EnrollDecision {
  if (!checkboxChecked) return 'skip';
  if (existingPasskeys > 0) return 'inform';
  return 'create';
}

// enrollErrText yields a short, human reason for a failed passkey create,
// mapping the common WebAuthn DOMExceptions to plain language.
export function enrollErrText(e: unknown): string {
  if (e instanceof DOMException) {
    switch (e.name) {
      case 'InvalidStateError':
        return 'a passkey for this account already exists on this device';
      case 'NotAllowedError':
        return 'the request was dismissed or timed out';
      case 'AbortError':
        return 'the request was cancelled';
      default:
        return e.name;
    }
  }
  if (e instanceof Error) return e.message;
  return 'unexpected error';
}
